/**
 * The homepage hero as it shipped BEFORE the CMS took over.
 *
 * This module is the single source of truth for two things that both need
 * the old, hard-coded values:
 *
 *  1. `scripts/migrate-hero-slides.mts` seeds `cms_hero_slides` from these
 *     values, so the site looks identical on the day the CMS goes live.
 *  2. `HeroSlider` falls back to them when the build-time snapshot is empty
 *     (no CMS reachable at build time, or the table is still empty on a
 *     fresh install). The homepage must never render a blank hero.
 *
 * The COPY is deliberately NOT here. The five slides' words live in the i18n
 * bundles under `home.hero.slide<n>.*`, and the migration must read the
 * EFFECTIVE value — the CMS override if an operator set one, the i18n
 * default otherwise — not the raw JSON. A copy of the JSON here would
 * silently revert every headline anyone had already edited in the admin.
 * The copy keys are referenced below; the values are resolved by the same
 * resolver the site uses.
 *
 * IMPORT CONSTRAINTS
 * ------------------
 * This file is imported by a Node script (the migration) as well as by the
 * browser bundle, so:
 *   - no `@/` alias (a Node script cannot resolve it),
 *   - no CSS imports,
 *   - the hero artwork is referenced by URL STRING, never imported. The four
 *     SVG filenames and the one PNG contain CJK characters and no extension
 *     (`/品格被看見.png` has one, but `/講座分享.svg` style names are the
 *     problem in general); Node's ESM resolver cannot load them, and Vite's
 *     `?url` transform does not apply outside a bundler. This is the same
 *     reason `HeroSlider.tsx` kept them as literals.
 */

import type { HeroGradientToken, HeroImagePositionToken } from './heroTokens'

/**
 * The legacy artwork, as root-relative paths.
 *
 * These are the five slides' artwork: the site's own illustrations in
 * `public/` and the photographs the CMS now holds in the media library. They
 * stay in `public/` (the design document is explicit that they are NOT
 * deleted); the migration copies them into the media library and points each
 * slide at the new media row.
 *
 * ⚠ ROOT-RELATIVE, NEVER ABSOLUTE
 * --------------------------------
 * These must be paths like `/品格被看見.png` or
 * `/uploads/2026-10/<uuid>.medium.webp`, not
 * `http://192.168.1.147:3006/...`. An absolute URL to a development box is
 * unreachable for every visitor outside the LAN, so the image silently
 * 404s in production while looking perfect on the developer's machine.
 *
 * `scripts/lint-hero-slots.ts` enforces it: three of the five paths were
 * absolute on 2026-11-04 and 404'd for every real visitor.
 */
export const LEGACY_HERO_IMAGES = {
  values: '/品格被看見.png',
  smartCindy: '/products/smart-cindy/smart-cindy-homepage-hero.jpg',
} as const

/** One of the five slides as it was hard-coded in `HeroSlider.tsx`. */
export interface LegacyHeroSlide {
  /** 1-based slide number, matching the `home.hero.slide<n>` i18n keys. */
  readonly index: number
  /** The i18n key prefix holding this slide's copy. */
  readonly copyKeyBase: string
  /** The artwork URL, or null for a slide that falls back to its icon. */
  readonly image: string | null
  /** The image's alt text, as it was hard-coded (Traditional Chinese). */
  readonly imageAlt: string
  /** Where the artwork sits inside the card. */
  readonly imagePosition: HeroImagePositionToken
  /** The icon shown on the card when there is no photo. */
  readonly icon: string
  /** The card's background gradient. */
  readonly gradient: HeroGradientToken
  /** Where the primary CTA button points. */
  readonly ctaLink: string
}

/**
 * The five legacy slides, in order.
 *
 * The order and every value here are copied verbatim from the constants that
 * used to live in `src/components/home/HeroSlider.tsx` — `SLIDE_IMAGES`,
 * `SLIDE_IMAGE_ALTS`, `SLIDE_IMAGE_POSITIONS`, `SLIDE_ICONS`,
 * `SLIDE_BACKGROUNDS` and `SLIDE_CTA_LINKS` — as they were at commit
 * `24684cf` (the last committed pre-CMS hero), less the AI 升學資訊 slide
 * that duplicated another, plus the SmartCindy slide that came with the CMS
 * work (now the fifth and last).
 *
 * The artwork was the site's own illustrations in `public/`; three of the
 * five slides now carry photographs the CMS holds in the media library, and
 * the English alt for each of them lives in
 * `src/content/heroImageAlts.ts`, where the descriptions match the pictures
 * exactly.
 *
 * `scripts/migrate-hero-slides.mts` reads this shape: a `/uploads/<uuid>`
 * path means "this photo is already in the media library — point the slide
 * at that row", and anything else means "a file in `public/` — ingest it".
 */
export const LEGACY_HERO_SLIDES: readonly LegacyHeroSlide[] = [
  {
    index: 1,
    copyKeyBase: 'home.hero.slide1',
    image: LEGACY_HERO_IMAGES.values,
    imageAlt:
      'ValuesIncubator+ 十五項品格卡：寬恕、合作、好奇、欣賞、勇敢、愛學習、仁慈、創意、毅力、判断、誠實、社交智能、謙遜、感恩、謹慎',
    imagePosition: 'center',
    icon: 'lucide:heart-handshake',
    gradient: 'green',
    ctaLink: '/products/values-incubator',
  },
  {
    index: 2,
    copyKeyBase: 'home.hero.slide2',
    image: '/uploads/2026-10/0404b8e5-a46f-4e28-9d23-6245effe0b77.medium.webp',
    imageAlt: '講座分享：講者向學校師生講解議題，螢幕上顯示上升的數據圖表',
    imagePosition: 'left',
    icon: 'lucide:megaphone',
    gradient: 'olive',
    ctaLink: '/lectures',
  },
  {
    index: 3,
    copyKeyBase: 'home.hero.slide3',
    image: '/products/bite-learn/assets/solution.jpg',
    imageAlt:
      'AI 微學習：手機自動派發短小微學習單元，把上學途中與用餐等零碎時間變成有效課室',
    imagePosition: 'left',
    icon: 'lucide:smartphone',
    gradient: 'gold',
    ctaLink: '/products/bite-learn',
  },
  {
    index: 4,
    copyKeyBase: 'home.hero.slide5',
    image: '/uploads/2026-11/fbf3b412-5ac1-4282-bf22-0959d8583583.small.webp',
    imageAlt: '國民教育：盾牌象徵憲制秩序與法治，書本代表國家歷史與文化，四根支柱承托四大核心',
    imagePosition: 'left',
    icon: 'lucide:landmark',
    gradient: 'brown',
    ctaLink: '/products',
  },
  {
    index: 5,
    copyKeyBase: 'home.hero.slide6',
    image: LEGACY_HERO_IMAGES.smartCindy,
    imageAlt:
      'SmartCindy AI 升學及就業顧問：戴上耳機的學生微笑作答，背景是香港城市天際線與 AI 網絡圖示',
    imagePosition: 'right',
    icon: 'lucide:bot',
    gradient: 'greenDark',
    ctaLink: '/products/smart-cindy',
  },
]
